import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';

import { principalConfirm } from 'api/Wizard/Wizard';

import { cn } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Button/Button';
import Dot from 'components/shared/Dot/Dot';
import Icon from 'components/shared/Icon/Icon';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Progress from 'components/shared/Progress/Progress';

import DefaultReviewComponent from './components/DefaultReviewComponent';

const Accordion = ({
  stepTitle,
  noPanel = false,
  panelComponent,
  ButtonComponent = null,
  panelClassName,
  className,
  navigateToSubstep,
  wizardInstanceId,
  data,
  invalidateQueryKeys = ['wizard-overview'],
  defaultOpen = false
}) => {
  const { practiceId } = useOutletContext();
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const [unconfirmationModalVisible, setUnconfirmationModalVisible] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const navigate = useNavigate();
  const isStepCompleted = data?.status === 'Completed' || data?.status === 'Confirmed';
  const isStepConfirmed = data?.status === 'Confirmed';

  const completedSubsteps = data?.substeps?.filter(
    (substep) => substep.status === 'Completed' || substep.status === 'Confirmed'
  )?.length;
  const completedSubstepsPercentage = data?.substeps
    ? Math.round((completedSubsteps / data.substeps.length) * 100)
    : 0;

  const { mutateAsync: principalConfirmation } = useCustomMutation({
    mutationFn: (data) => {
      return principalConfirm(navigate, {
        wizard_instance_id: wizardInstanceId,
        substep_id: data.substep_id,
        step_id: data.step_id,
        action: data.action,
        practiceId
      });
    },
    invalidateQueryKey: invalidateQueryKeys
  });

  const handleConfirmClick = (e) => {
    e.stopPropagation();
    setConfirmationModalVisible(true);
  };

  const handleConfirmContinue = async () => {
    setIsConfirming(true);
    await principalConfirmation({
      wizard_instance_id: wizardInstanceId,
      step_id: data.step_id,
      action: 'confirm'
    });
    setIsConfirming(false);
    setConfirmationModalVisible(false);
  };

  const handleUnconfirmClick = () => {
    setUnconfirmationModalVisible(true);
  };

  const handleUnconfirmContinue = async () => {
    setIsConfirming(true);
    await principalConfirmation({
      wizard_instance_id: wizardInstanceId,
      step_id: data.step_id,
      action: 'unconfirm'
    });
    setIsConfirming(false);
    setUnconfirmationModalVisible(false);
  };

  return (
    <>
      <Disclosure
        as="div"
        defaultOpen={defaultOpen}
        className={cn(
          'overflow-hidden rounded-lg border border-solid border-neutral-200',
          className
        )}>
        {({ open }) => (
          <>
            <DisclosureButton
              className={`flex w-full items-center justify-between ${
                open ? 'border-b border-solid border-neutral-200 last:border-b-0' : ''
              } px-2 py-1`}>
              {ButtonComponent ? (
                React.isValidElement(ButtonComponent) ? (
                  React.cloneElement(ButtonComponent, { open })
                ) : (
                  <ButtonComponent open={open} />
                )
              ) : (
                <>
                  <div className="flex w-fit items-center gap-2 py-0.5">
                    <Icon
                      size={20}
                      color="neutral"
                      shade={700}
                      icon={!open ? 'chevron-right-linear' : 'chevron-down-linear'}
                    />
                    {isStepConfirmed ? (
                      <Icon size={28} icon="tick-circle-filled" color="success" />
                    ) : isStepCompleted ? (
                      <Icon size={28} icon="tick-circle-linear" color="success" />
                    ) : (
                      <Progress
                        className="bg-neutral-50"
                        percentage={completedSubstepsPercentage}
                      />
                    )}
                    <p className="text-sm font-500">{stepTitle}</p>
                    {isStepConfirmed && (
                      <div className="ml-2 flex items-center gap-1 rounded-2xl border border-solid border-black border-opacity-15 bg-success-100 px-2 py-0.5 text-xs font-500">
                        <Dot color="success" size="sm" />
                        Confirmed
                      </div>
                    )}
                  </div>
                  <div className="flex w-fit items-center gap-2">
                    <p className="text-xs">
                      Completed <b>{completedSubsteps ?? 0}</b> out of{' '}
                      <b>{data?.substeps?.length ?? 0}</b>
                    </p>
                    <p className="mx-1">/</p>
                    {isStepConfirmed ? (
                      <>
                        <Popover
                          isFixed
                          isBorder
                          position="left"
                          options={[
                            {
                              label: 'Unconfirm',
                              icon: 'minus-circle-mono',
                              onClick: handleUnconfirmClick
                            }
                          ]}
                          buttonClassName="border border-solid border-neutral-200 p-1 !min-w-6 !h-6 bg-white"
                        />
                      </>
                    ) : (
                      <Button
                        size="xs"
                        color="neutral"
                        style="outlined"
                        text="Confirm"
                        shade={700}
                        onClick={handleConfirmClick}
                        icon="checkbox-circle-mono"
                      />
                    )}
                  </div>
                </>
              )}
            </DisclosureButton>
            {!noPanel && (
              <DisclosurePanel className={cn('bg-white p-5', panelClassName)}>
                {panelComponent ?? (
                  <DefaultReviewComponent navigateToSubstep={navigateToSubstep} data={data} />
                )}
              </DisclosurePanel>
            )}
          </>
        )}
      </Disclosure>

      <Confirm
        icon="info-circle-filled"
        iconColor="primary"
        variant="primary"
        isOpen={confirmationModalVisible}
        handleClose={() => setConfirmationModalVisible(false)}
        title="Confirm Step?"
        message="Are you sure you want to confirm this step? This action will mark the step as confirmed."
        footerActions={{
          primary: {
            text: 'Confirm',
            onClick: handleConfirmContinue,
            isLoading: isConfirming
          }
        }}
      />

      <Confirm
        icon="info-circle-filled"
        iconColor="warning"
        variant="warning"
        isOpen={unconfirmationModalVisible}
        handleClose={() => setUnconfirmationModalVisible(false)}
        title="Unconfirm Step?"
        message="Are you sure you want to unconfirm this step? This will revert the step back to a non-confirmed state."
        footerActions={{
          primary: {
            text: 'Unconfirm',
            onClick: handleUnconfirmContinue,
            isLoading: isConfirming
          }
        }}
      />
    </>
  );
};

export default Accordion;
